import { createSelector } from 'reselect';
import axios from 'axios';

import { getServiceEndpoints } from 'reducers/configs/serviceEnpoints';
import { getClientCode, getSessionKey } from 'reducers/Login';
import { getCurrentPosLanguage } from 'reducers/configs/settings';

/**
 * @example
 * {
 *     "data": {
 *         "customer_id": 1,
 *         "last_transaction_id": 26733,
 *         "last_transaction_number": "14007995",
 *         "last_visit": "2023-10-17 11:36:57",
 *         "first_visit": "2014-09-30 15:59:17",
 *         "total_visits": "6078",
 *         "total_sales": "66.85",
 *         "total_savings": "900.2374",
 *         "total_dsc_savings": "43.119",
 *         "potential_savings": "73.0635",
 *         "dsc_current_savings": "32.5"
 *     }
 * }
 */
type GetCustomerStatisticsResponse = {
  data: {
    customer_id: number;
    last_transaction_id: number;
    last_transaction_number: string;
    last_visit: string;
    first_visit: string;
    total_visits: string;
    total_sales: string;
    total_savings: string;
    total_dsc_savings: string;
    potential_savings: string;
    dsc_current_savings: string;
  };
};

/**
 * @example
 * {
 *     "WBU_53303": [
 *         "data": {
 *             "sales_document_id": 0,
 *             "sales_document_number": "WBU_53303",
 *             "date_time": "2021-03-21 15:54:07",
 *             "product_id": 1630,
 *             "product_name": "Cyl Seed No-Mess HP",
 *             "code": "W10702",
 *             "amount": 1,
 *             "row_net_total": 13.59
 *         }
 *     ]
 * }
 */
export type GetRecentSalesResponse = {
  status: {
    responseStatus: string;
    recordsTotal: number;
    lastId: number;
    generationTime: number;
  };
  data: Record<
    string,
    {
      sales_document_id: number;
      sales_document_number: string;
      date_time: string;
      product_id: number;
      product_name: string;
      code: string;
      amount: number;
      row_net_total: number;
    }[]
  >;
};

const getAxiosInstance = createSelector(
  getServiceEndpoints,
  getCurrentPosLanguage,
  getSessionKey,
  getClientCode,
  (serviceEndpoints, lang, sessionKey, clientCode) => {
    const SUFFIX = 'api/';
    const REPLACEMENT = `${clientCode}/${lang}/`;
    const url: string = serviceEndpoints.goerp?.url;
    if (!url) throw new Error('Service endpoints not loaded');
    if (!url.endsWith(SUFFIX))
      throw new Error(`goERP endpoint (${url}) does not end with 'api/'`);
    return axios.create({
      baseURL: url.slice(0, -SUFFIX.length) + REPLACEMENT,
      withCredentials: true,
      headers: { clientCode, sessionKey }
    });
  },
);

export const getTemplateEngineApi = createSelector(getAxiosInstance, ax => ({
  /** https://template-engine-eu10.erply.com/520863/en/wbu-get-customer-statistics.json?id=1 */
  getCustomerStatistics: (params: { id: number }) => {
    return ax.get<GetCustomerStatisticsResponse>(
      'wbu-get-customer-statistics.json',
      { params },
    );
  },
  getCustomerRecentSales: (params: {
    customerId?: number;
    pageNo?: number;
    perPage?: number;
  }) => {
    return ax.get<GetRecentSalesResponse>(
      'sales-history-customer-recent-sales.json',
      {
        params,
      },
    );
  },
}));
